import React, { useState, useId } from 'react';
import type { Tone } from '../../domain/types';
import { ChevronIcon } from './ChevronIcon';

export interface AccordionProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  badge?: React.ReactNode;
  icon?: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  isOpen?: boolean;
  onToggle?: (nextOpen: boolean) => void;
  tone?: Tone;
  className?: string;
  style?: React.CSSProperties;
}

export const Accordion: React.FC<AccordionProps> = ({
  title,
  subtitle,
  badge,
  icon,
  children,
  defaultOpen = false,
  isOpen: controlledIsOpen,
  onToggle,
  tone,
  className = '',
  style
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = controlledIsOpen !== undefined;
  const isExpanded = isControlled ? controlledIsOpen : uncontrolledOpen;

  const accordionId = useId();
  const headerId = `z-acc-header-${accordionId}`;
  const panelId = `z-acc-panel-${accordionId}`;

  const handleToggle = () => {
    const next = !isExpanded;
    if (!isControlled) {
      setUncontrolledOpen(next);
    }
    onToggle?.(next);
  };

  const getToneFill = () => {
    if (!tone) return 'var(--z-surface)';
    switch (tone) {
      case 'ok':
        return 'var(--z-ok-bg)';
      case 'warn':
        return 'var(--z-warn-bg)';
      case 'bad':
        return 'var(--z-bad-bg)';
      case 'accent':
        return 'var(--z-accent-soft)';
      case 'flat':
      default:
        return 'var(--z-fill)';
    }
  };

  return (
    <div
      className={`z-accordion ${isExpanded ? 'z-accordion-open' : ''} ${className}`}
      style={{
        border: 'none',
        borderRadius: 'var(--z-radius-md, 10px)',
        backgroundColor: getToneFill(),
        overflow: 'hidden',
        transition: 'background-color var(--z-ease-fast, 150ms)',
        fontFamily: 'var(--font-sans)',
        boxShadow: 'var(--z-sh-4)',
        ...style
      }}
    >
      <button
        id={headerId}
        type="button"
        aria-expanded={isExpanded}
        aria-controls={panelId}
        onClick={handleToggle}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          backgroundColor: isExpanded ? 'var(--z-surface-2)' : 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          gap: '12px',
          userSelect: 'none',
          transition: 'background-color var(--z-ease-fast, 150ms)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
          {icon && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: 'var(--z-text-2)'
              }}
              aria-hidden="true"
            >
              {icon}
            </span>
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600, fontSize: '14px', color: 'var(--z-text)' }}>
                {title}
              </span>
              {badge && <span>{badge}</span>}
            </div>
            {subtitle && (
              <div
                style={{
                  fontSize: '12px',
                  color: 'var(--z-text-3)',
                  marginTop: '2px'
                }}
              >
                {subtitle}
              </div>
            )}
          </div>
        </div>

        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--z-text-3)',
            flexShrink: 0,
          }}
          aria-hidden="true"
        >
          <ChevronIcon
            size={16}
            direction={isExpanded ? 'up' : 'down'}
            animated
          />
        </span>
      </button>

      {isExpanded && (
        <div
          id={panelId}
          role="region"
          aria-labelledby={headerId}
          className="animate-zfade-in"
          style={{
            padding: '16px',
            borderTop: 'none',
            backgroundColor: 'var(--z-surface-subtle)'
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};
